import { useState } from 'react';
import clsx from 'clsx';
import { Flex } from '@nula/core-ui';
import { emojiUrl, emojiSpriteUrl } from '@/lib/emojis/emoji-url';
import { SPRITE_INDEX, SPRITE_CELL } from '@/lib/emojis/emoji-sprite';
import type { EmojiVariant } from './types';
import styles from './emoji-renderer.module.css';

interface EmojiRendererProps {
  group: string;
  id: string;
  size: number;
  variant: EmojiVariant;
  alt: string;
  className?: string;
  /** Use sprite sheet instead of individual SVG. Faster for picker grids. */
  useSprite?: boolean;
}

export function EmojiRenderer({ group, id, size, variant, alt, className, useSprite }: EmojiRendererProps) {
  const [errored, setErrored] = useState(false);

  if (useSprite) {
    const pos = SPRITE_INDEX[group]?.[id];
    if (pos) {
      const scale = size / SPRITE_CELL;
      const [col, row] = pos;
      return (
        <Flex
          align="center"
          justify="center"
          className={clsx(styles.root, className)}
          style={{ '--emoji-size': `${size}px` } as React.CSSProperties}
        >
          <div
            aria-label={alt}
            role="img"
            className={styles.sprite}
            style={{
              '--sprite-size': `${size}px`,
              '--sprite-image': `url(${emojiSpriteUrl(group)})`,
              '--sprite-bg-size': `${SPRITE_CELL * 8 * scale}px auto`,
              '--sprite-bg-x': `-${col * size}px`,
              '--sprite-bg-y': `-${row * size}px`,
            } as React.CSSProperties}
          />
        </Flex>
      );
    }
  }

  const src = emojiUrl(group, id);

  return (
    <Flex
      align="center"
      justify="center"
      className={clsx(styles.root, className)}
      style={{ '--emoji-size': `${size}px` } as React.CSSProperties}
    >
      <img
        src={src}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        className={clsx(styles.img, variant === 'stamp' && styles.stamp)}
        style={variant === 'stamp' && !errored
          ? { '--stamp-border': `${Math.max(1, Math.round(size * 0.02))}px` } as React.CSSProperties
          : errored ? { display: 'none' } : undefined}
        onError={() => setErrored(true)}
      />
      {errored && <span aria-hidden="true" className={styles.fallback} />}
    </Flex>
  );
}
